import type { AgGridReact } from "ag-grid-react";
import React from "react";

export type UseRefreshServerSideWhenParams<T> = {
  gridRef: React.RefObject<AgGridReact<T> | null>;
  /**
   * When any value changes after mount, calls `refreshServerSide({ purge: true })`.
   * Safe to pass a fresh inline array each render (values are shallow-compared).
   */
  deps: readonly unknown[];
  /** If true (default), the first effect run after mount does nothing — avoids double-fetch on load. */
  skipInitial?: boolean;
};

function useStableDependencyList(deps: readonly unknown[]): readonly unknown[] {
  const ref = React.useRef<readonly unknown[]>(deps);
  if (
    deps.length !== ref.current.length ||
    deps.some((v, i) => !Object.is(v, ref.current[i]))
  ) {
    ref.current = deps;
  }
  return ref.current;
}

/**
 * SSRM caches loaded blocks. Updating the datasource factory / closure alone does not guarantee a
 * purge; external inputs (search text, route tab, etc.) need an explicit refresh.
 */
export function useRefreshServerSideWhen<T>(
  params: UseRefreshServerSideWhenParams<T>,
): void {
  const { gridRef, deps, skipInitial = true } = params;
  const skipRef = React.useRef(skipInitial);
  const stableDeps = useStableDependencyList(deps);

  React.useEffect(() => {
    skipRef.current = skipInitial;
  }, [skipInitial]);

  React.useEffect(() => {
    void stableDeps;
    if (skipRef.current) {
      skipRef.current = false;
      return;
    }
    gridRef.current?.api.refreshServerSide({ purge: true });
  }, [gridRef, stableDeps]);
}
